import React, { useState } from 'react';
import CopyButton from './CopyButton';

const BASE = (process.env.REACT_APP_API_URI || 'http://localhost:5100/api').replace('/api', '');

const LANGS = [
  { key: 'python',   label: 'Python'    },
  { key: 'pandas',   label: 'Pandas'    },
  { key: 'hf',       label: 'HuggingFace' },
  { key: 'pytorch',  label: 'PyTorch'   },
  { key: 'curl',     label: 'curl'      },
  { key: 'js',       label: 'JavaScript' },
  { key: 'r',        label: 'R'         },
];

function makeSnippets(assetId, title, apiKey, format) {
  const url    = `${BASE}/api/assets/${assetId}/data?format=${format}`;
  const urlKey = apiKey ? `${url}&api_key=${apiKey}` : `${url}&api_key=YOUR_API_KEY`;
  const hdr    = apiKey ? `"Authorization: Bearer ${apiKey}"` : '"Authorization: Bearer YOUR_API_KEY"';
  const safe   = title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

  return {
    python: `import requests, json

url = "${urlKey}"
response = requests.get(url)
data = response.json()

rows = data["rows"]
print(f"Loaded {len(rows)} rows")
print("Columns:", data["meta"]["columns"])

# Пример обработки
for row in rows[:5]:
    print(row)`,

    pandas: `import pandas as pd

# Прямая загрузка датасета
df = pd.read_${format === 'csv' ? 'csv' : 'json'}("${urlKey}"${format === 'json' ? ', orient="records"' : ''})

print(df.shape)
print(df.head())
print(df.dtypes)

# Сохранить локально
df.to_csv("${safe}.csv", index=False)`,

    hf: `from datasets import load_dataset

# Загрузка через HuggingFace datasets
ds = load_dataset(
    "${format === 'csv' ? 'csv' : 'json'}",
    data_files="${urlKey}",
    split="train"
)

print(ds)
print(ds[0])

# Конвертировать в pandas
df = ds.to_pandas()`,

    pytorch: `import torch
from torch.utils.data import Dataset, DataLoader
import requests

class dApiaryDataset(Dataset):
    def __init__(self, asset_id, api_key, transform=None):
        url = f"${BASE}/api/assets/{asset_id}/data?format=json&api_key={api_key}"
        self.data = requests.get(url).json()["rows"]
        self.cols = requests.get(url).json()["meta"]["columns"]
        self.transform = transform

    def __len__(self):
        return len(self.data)

    def __getitem__(self, idx):
        item = self.data[idx]
        return self.transform(item) if self.transform else item

# Использование
dataset = dApiaryDataset("${assetId}", "${apiKey || 'YOUR_API_KEY'}")
loader  = DataLoader(dataset, batch_size=32, shuffle=True)

for batch in loader:
    # ваш код обучения
    pass`,

    curl: `# JSON формат
curl -H ${hdr} \\
  "${urlKey}" | python3 -m json.tool

# CSV формат
curl -H ${hdr} \\
  "${BASE}/api/assets/${assetId}/data?format=csv${apiKey ? '&api_key=' + apiKey : '&api_key=YOUR_KEY'}" \\
  -o ${safe}.csv

# JSONL (построчный JSON)
curl -H ${hdr} \\
  "${BASE}/api/assets/${assetId}/data?format=jsonl${apiKey ? '&api_key=' + apiKey : '&api_key=YOUR_KEY'}" \\
  -o ${safe}.jsonl

# Схема датасета (колонки, типы)
curl "${BASE}/api/assets/${assetId}/schema?api_key=${apiKey || 'YOUR_KEY'}"`,

    js: `// Node.js / Browser
const API_KEY = "${apiKey || 'YOUR_API_KEY'}";
const ASSET_ID = "${assetId}";

async function loadDataset(format = "json") {
  const url = \`${BASE}/api/assets/\${ASSET_ID}/data?format=\${format}&api_key=\${API_KEY}\`;
  const res = await fetch(url, {
    headers: { "Authorization": \`Bearer \${API_KEY}\` }
  });
  return res.json();
}

// Загрузка
const { rows, meta } = await loadDataset("json");
console.log(\`Loaded \${rows.length} rows, columns: \${meta.columns}\`);

// Стриминг JSONL (для больших датасетов)
const stream = await fetch(\`${BASE}/api/assets/\${ASSET_ID}/data?format=jsonl&api_key=\${API_KEY}\`);
const reader = stream.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  const lines = decoder.decode(value).split("\\n").filter(Boolean);
  lines.forEach(line => {
    const row = JSON.parse(line);
    // обработка строки...
  });
}`,

    r: `library(httr)
library(jsonlite)

api_key <- "${apiKey || 'YOUR_API_KEY'}"
asset_id <- "${assetId}"

# JSON формат
url <- paste0("${BASE}/api/assets/", asset_id, "/data?format=json&api_key=", api_key)
response <- GET(url)
data <- fromJSON(content(response, "text"))
df <- as.data.frame(data$rows)
print(head(df))

# CSV формат
csv_url <- paste0("${BASE}/api/assets/", asset_id, "/data?format=csv&api_key=", api_key)
df_csv <- read.csv(csv_url)
summary(df_csv)`,
  };
}

export default function AssetApiTab({ asset, userApiKey }) {
  const [activeLang, setActiveLang] = useState('python');
  const [format, setFormat] = useState('json');

  const snippets = makeSnippets(asset._id, asset.title, userApiKey, format);
  const code     = snippets[activeLang] || '';

  const dataUrl  = `${BASE}/api/assets/${asset._id}/data?format=${format}${userApiKey ? '&api_key=' + userApiKey : ''}`;
  const schemaUrl = `${BASE}/api/assets/${asset._id}/schema${userApiKey ? '?api_key=' + userApiKey : ''}`;

  return (
    <div>
      {/* Dataset URL */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Dataset URL</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {['json', 'csv', 'jsonl'].map(f => (
            <button key={f} onClick={() => setFormat(f)} style={{
              padding: '4px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12,
              fontWeight: 600, textTransform: 'uppercase', fontFamily: 'Space Mono',
              border: `1px solid ${format === f ? 'var(--accent)' : 'var(--border)'}`,
              background: format === f ? 'var(--accent-bg)' : 'var(--surface)',
              color: format === f ? 'var(--accent)' : 'var(--text-2)',
            }}>{f}</button>
          ))}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
          background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)',
        }}>
          <code style={{ flex: 1, fontSize: 12, fontFamily: 'Space Mono', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dataUrl}
          </code>
          <CopyButton text={dataUrl} label="Copy URL" />
        </div>
      </div>

      {/* Schema URL */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Schema</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <code style={{ flex: 1, fontSize: 12, fontFamily: 'Space Mono', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {schemaUrl}
          </code>
          <CopyButton text={schemaUrl} label="Copy" />
        </div>
      </div>

      {/* Warn if no API key */}
      {!userApiKey && (
        <div style={{ padding: '11px 14px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 13, color: '#92400E', marginBottom: 20 }}>
          <b>Get an API key</b> in <a href="/dashboard/settings" style={{ color: 'var(--accent)' }}>Settings → API Keys</a> to use snippets below with authentication.
        </div>
      )}

      {/* Snippets */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Code snippets</div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 0, borderBottom: '1px solid var(--border)' }}>
        {LANGS.map(l => (
          <button key={l.key} onClick={() => setActiveLang(l.key)} style={{
            padding: '7px 14px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: activeLang === l.key ? 600 : 400,
            color: activeLang === l.key ? 'var(--accent)' : 'var(--text-2)',
            borderBottom: `2px solid ${activeLang === l.key ? 'var(--accent)' : 'transparent'}`,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            {l.label}
          </button>
        ))}
      </div>

      <div style={{ position: 'relative', marginTop: -1 }}>
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
          <CopyButton text={code} label="Copy code" />
        </div>
        <pre style={{
          margin: 0, padding: '16px 14px', paddingRight: 100,
          background: '#1E293B', color: '#E2E8F0',
          fontFamily: 'Space Mono', fontSize: 12, lineHeight: 1.6,
          overflowX: 'auto', borderRadius: '0 0 8px 8px',
          border: '1px solid var(--border)', borderTop: 'none',
          maxHeight: 420, overflowY: 'auto',
        }}>
          {code}
        </pre>
      </div>

      {/* Access policy */}
      <div style={{ marginTop: 20, padding: '12px 14px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-2)' }}>
        {asset.price === 0
          ? 'This asset is free — no API key required for reading data.'
          : 'This asset requires purchase or active subscription before API access is granted.'}
        {' '}
        Limits: up to 10,000 rows per request, use <code style={{ fontFamily: 'Space Mono', background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>?page=N</code> for pagination.
      </div>
    </div>
  );
}
